import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, CheckCircle2, RefreshCw, LogOut, AlertCircle, Radio } from "lucide-react";

/**
 * Per-user "Connect Gmail" card. Mounts in the Triage left sidebar.
 * - If GMAIL_CLIENT_ID/SECRET not set on backend: shows a faint "not configured" badge (admin only).
 * - If not connected: shows a Connect button that opens Google OAuth in a new window.
 * - If connected: shows the connected email + last-synced timestamp + Sync now / Disconnect.
 */
export default function GmailConnect() {
  const [state, setState] = useState({
    configured: false,
    connected: false,
    connected_email: null,
    last_synced_at: null,
    requires_reauth: false,
    reauth_reason: "",
  });
  // Iter 110h — Guard against the "Ask your admin to set GMAIL_CLIENT_ID"
  // banner flashing for ~half a second on every page load. The default
  // state has `configured=false`, which used to render the warning
  // banner immediately and then swap to the connected card once
  // `/gmail/config` responded. Now we hide the entire card until the
  // first config fetch resolves (success OR error) — the user simply
  // sees nothing in the slot for ~200 ms instead of a misleading
  // warning that quickly disappears.
  const [configLoaded, setConfigLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  // Iter 110 — Live pill state. Tracks the most recent SSE event so we
  // can display "Live · just now" inside the connected card alongside
  // "Last sync · …". `lastLiveAt` ticks the rendered relative time
  // every 15 s so it stays accurate without re-fetching anything.
  const [lastLiveAt, setLastLiveAt] = useState(null);
  const [pulse, setPulse] = useState(false);
  const pulseTimerRef = useRef(null);
  useEffect(() => {
    const onSse = (e) => {
      const t = e?.detail?.type;
      // Only count real inbox events for the "Live" pill — ignore the
      // heartbeat/hello echo so the dot doesn't flash on every keepalive.
      if (t === "email.new" || t === "email.updated" || t === "email.deleted") {
        setLastLiveAt(Date.now());
        setPulse(true);
        if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = setTimeout(() => setPulse(false), 1500);
      }
    };
    window.addEventListener("close:inbox-event", onSse);
    return () => {
      window.removeEventListener("close:inbox-event", onSse);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, []);
  // Tick the rendered relative-time string every 15 s.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastLiveAt) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [lastLiveAt]);

  const liveLabel = (() => {
    if (!lastLiveAt) return null;
    const ageMs = Date.now() - lastLiveAt;
    if (ageMs < 5_000) return "just now";
    if (ageMs < 60_000) return `${Math.floor(ageMs / 1000)} s ago`;
    if (ageMs < 60 * 60_000) return `${Math.floor(ageMs / 60_000)} m ago`;
    return `${Math.floor(ageMs / 3_600_000)} h ago`;
  })();

  const load = async () => {
    try {
      const r = await api.get("/gmail/config");
      setState(r.data);
    } catch {
      /* ignore */
    } finally {
      setConfigLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // If we came back from OAuth redirect, refresh state + toast.
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") {
      toast.success("Gmail connected. Syncing your inbox…");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 800);
    } else if (params.get("gmail_error")) {
      toast.error(`Gmail connect failed: ${params.get("gmail_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gmail/auth-url");
      if (r.data?.auth_url) {
        window.location.href = r.data.auth_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Gmail auth");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gmail/sync");
      toast.success(`${r.data.synced} new message${r.data.synced === 1 ? "" : "s"} pulled.`);
      await load();
    } catch (e) {
      // 409 = backend has flagged this connection as needing re-consent.
      // Reload config so the UI flips to the "Reconnect Gmail" CTA instead
      // of leaving the user wondering why nothing is syncing.
      if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail || "Gmail access was revoked. Please reconnect.");
        await load();
      } else {
        toast.error(e?.response?.data?.detail || "Sync failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail from Close? Existing synced messages stay.")) return;
    setBusy(true);
    try {
      await api.post("/gmail/disconnect");
      toast.success("Gmail disconnected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  // Iter 110h — Suppress the entire card until the first /gmail/config
  // response settles. Prevents the "GMAIL_CLIENT_ID not set" banner from
  // flashing on every Triage mount.
  if (!configLoaded) {
    return <div className="mx-4 my-3 h-[58px]" aria-hidden="true" data-testid="gmail-connect-loading"/>;
  }

  if (!state.configured) {
    return (
      <div className="mx-4 my-3 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="gmail-not-configured">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-slate-400 mt-0.5 shrink-0"/>
          <div className="text-[11px] leading-relaxed text-slate-600">
            <div className="font-medium text-slate-700 mb-0.5">Gmail not configured</div>
            Ask your admin to set <code className="text-[10px]">GMAIL_CLIENT_ID</code> in backend.
          </div>
        </div>
      </div>
    );
  }

  if (!state.connected) {
    return (
      <div className="mx-4 my-3 rounded-md border border-slate-200 bg-white p-3" data-testid="gmail-connect-card">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="h-4 w-4 text-red-600"/>
          <div className="text-sm font-medium text-slate-900">Connect Gmail</div>
        </div>
        <p className="text-[11px] leading-relaxed text-slate-500 mb-2.5">
          Mirror your Gmail inbox into Close and reply from here.
        </p>
        <Button
          size="sm"
          onClick={connect}
          disabled={busy}
          data-testid="gmail-connect-button"
          className="w-full bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs"
        >
          {busy ? "Opening Google…" : "Connect your inbox"}
        </Button>
      </div>
    );
  }

  if (state.connected && state.requires_reauth) {
    return (
      <div className="mx-4 my-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="gmail-reauth-card">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="h-4 w-4 text-amber-600"/>
          <div className="text-sm font-medium text-slate-900">Gmail access revoked</div>
        </div>
        <div className="text-[11px] text-slate-600 truncate mb-1.5" title={state.connected_email}>
          {state.connected_email}
        </div>
        <p className="text-[11px] leading-relaxed text-amber-900/80 mb-2.5">
          New messages won&apos;t sync until you reconnect. Your existing inbox stays intact.
        </p>
        <Button
          size="sm"
          onClick={connect}
          disabled={busy}
          data-testid="gmail-reconnect-button"
          className="w-full bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
        >
          {busy ? "Opening Google…" : "Reconnect Gmail"}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-3 my-2 rounded-md border border-emerald-200 bg-emerald-50/50 px-2.5 py-2" data-testid="gmail-connected-card">
      <div className="flex items-center gap-1.5 mb-0.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0"/>
        <div className="text-[12px] font-medium text-slate-900 truncate">Gmail connected</div>
      </div>
      <div className="text-[10px] text-slate-600 truncate mb-1" title={state.connected_email}>
        {state.connected_email}
      </div>
      {state.last_synced_at && (
        <div
          className="text-[9px] text-slate-400 mb-1.5 tabular-nums flex items-center justify-between gap-1.5"
          data-testid="gmail-sync-meta"
        >
          <span
            title="Last time the backend checked Gmail for changes (every ~2 min via background poll, instantly via Pub/Sub push, or when you click Sync). It does NOT mean the time of the most recent email — that's what the Live pill on the right shows."
            className="cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2 truncate"
            data-testid="gmail-last-sync"
          >
            Last sync · {new Date(state.last_synced_at).toLocaleTimeString()}
          </span>
          {/* Iter 110 — "Live" pill. Pulses green for ~1.5 s every time
              the SSE bus delivers a new inbox event (email.new /
              email.updated / email.deleted), then settles into a
              relative-time hint. Hidden until the first event of the
              session so users with no traffic don't see a misleading
              "Live · …" with no source of truth. */}
          {liveLabel ? (
            <span
              className={[
                "inline-flex items-center gap-1 rounded-full border px-1 py-[1px] text-[9px] tracking-wide transition-colors shrink-0",
                pulse
                  ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-500",
              ].join(" ")}
              title={`Last live update · ${new Date(lastLiveAt).toLocaleTimeString()}`}
              data-testid="gmail-live-pill"
            >
              <Radio
                className={`h-2 w-2 ${
                  pulse ? "text-emerald-600 animate-pulse" : "text-slate-400"
                }`}
                strokeWidth={2.5}
              />
              Live · {liveLabel}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-1 py-[1px] text-[9px] text-slate-400 shrink-0"
              title="Real-time inbox push is connected. The first inbox change will mark this Live."
              data-testid="gmail-live-pill-idle"
            >
              <Radio className="h-2 w-2" strokeWidth={2.5} />
              Live
            </span>
          )}
        </div>
      )}
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={sync} disabled={busy} data-testid="gmail-sync-button" className="flex-1 h-6 text-[10px] px-2" title="Full sync — pulls every new message from Gmail">
          <RefreshCw className={`h-2.5 w-2.5 mr-1 ${busy ? "animate-spin" : ""}`}/> Sync
        </Button>
        <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy} data-testid="gmail-disconnect-button" className="h-6 w-6 p-0 text-slate-500 hover:text-red-600">
          <LogOut className="h-2.5 w-2.5"/>
        </Button>
      </div>
    </div>
  );
}
